<template>
    <div class="bg-white rounded shadow-sm mb-3">
        <div class="d-flex flex-row align-items-center justify-content-between mb-3" style="padding: 20px">
            <h3 class="h5 font-weight-semibold">Detail Mapping QR Code</h3>
            <div class="d-flex flex-row align-items-center">
                <div class="d-flex flex-row align-items-center">
                    <div style="background-color: white; border-radius: 4px; position: relative; border: 0">
                        <span style="pointer-events: none; position: absolute; top: 0; bottom: 0; inset-inline-start: 0px; width: 40px; place-content: center; color: rgb(107 114 128); display: grid;">
                            <img src="/icon/iconly-calendar.svg" alt="calendar" style="width: 16px; height: 16px; object-fit: contain;" />
                        </span>
                        <flat-pickr ref="datepicker" v-model="selected.date" :config="dateRangeConfig"></flat-pickr>
                    </div>
                    <div style="width: 200px; margin-left: 20px;">
                        <MultiSelect
                            v-model="filter.status"
                            :options="[...options.status.map(item => item.label)]"
                            class="qtrust-select"
                            placeholder="All Mapping Times"
                        ></MultiSelect>
                    </div>
                </div>
            </div>
        </div>

        <div class="w-100 overflow-auto">
            <table class="table w-100 qtrust-table">
                <thead>
                    <tr>
                        <th>Nomor Mesin</th>
                        <th>Kode MPM</th>
                        <th>Kode AHM</th>
                        <th>Tanggal Mapping Terakhir</th>
                        <th>Mapping Times</th>
                        <th>Mapping History</th>
                    </tr>
                </thead>
                <tbody v-if="data">
                    <tr v-for="dat in data" :key="dat.qrcode || dat.engine_no || dat.loc_code">
                        <td>{{ dat.engine_no ? dat.engine_no : '-' }}</td>
                        <td>{{ dat.loc_code ? dat.loc_code : '-' }}</td>
                        <td>{{ dat.ahm_id ? dat.ahm_id : '-' }}</td>
                        <td>{{ dat.latest_scan_date ? moment(dat.latest_scan_date).format('DD MMM YYYY | HH:mm') : '-' }}</td>
                        <td>{{ dat.total_count ? dat.total_count : '0' }}x</td>
                        <td>
                            <button 
                                @click="showHistory(dat)"
                                type="button" style="padding: 0; margin: 0; background: transparent; box-shadow: none; color: #F36F21;">See History</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="d-flex justify-content-end align-items-center py-2 px-3" v-if="data">
            <p class="text-muted mb-0">Showing {{ (config.page-1)*config.limit+1 }}-{{ (config.page-1)*config.limit+data.length }} of {{ config.total }} items</p>
            <div class="d-flex align-items-center text-muted ml-3">
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(1)"><i class="fa fa-angle-double-left"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(config.page - 1)"><i class="fa fa-angle-left"></i></button>
                <p class="px-2 text-dark mb-0">{{ config.page }}</p>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.page + 1)"><i class="fa fa-angle-right"></i></button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= config.lastPage" @click="goToPage(config.lastPage)"><i class="fa fa-angle-double-right"></i></button>
            </div>
        </div>
        <modal
            v-if="modal.modalHistory"
            box-class="col-11 col-md-8 col-lg-6"
            :box-style="{
                'background': 'white',
                'margin': '0 auto',
                'border-radius': '8px',
                'overflow': 'hidden',
                'box-shadow': '0 0 10px 0 rgba(0, 0, 0, 0.1)',
                'padding': '0'
            }"
            @closeModal="modal.modalHistory = false"
        >
            <modal-history
                :data="modal.dataHistory"
                :start_date="filter.start_date"
                :end_date="filter.end_date"
                @closeModal="modal.modalHistory = false"
            ></modal-history>
        </modal>
    </div>
</template>

<script>
    import moment from 'moment';
    import debounce from 'debounce';
    import modal from '@/components/modal.vue';
    import modalHistory from './modalHistory.vue';

    // Single source of truth for the Mapping Times filter. The first entry is
    // the default ("All" still means at least 2 mappings: a QR mapped only once
    // is not a repeated mapping). A null times_max means no upper bound.
    const MAPPING_TIMES_OPTIONS = [
        {label: 'All Mapping Times', times_min: 2, times_max: null},
        {label: '2 Times Only', times_min: 2, times_max: 2},
        {label: '3 - 4 Times', times_min: 3, times_max: 4},
        {label: '5 Times & Above', times_min: 5, times_max: null},
        {label: '10 Times & Above', times_min: 10, times_max: null},
    ];
    const DEFAULT_MAPPING_TIMES = MAPPING_TIMES_OPTIONS[0];

    export default {
        props: ['engine_no'],
        data () {
            let start = moment().subtract(14, 'days').format("YYYY-MM-DD");
            let end = moment().format("YYYY-MM-DD");
            return {
                moment: moment,
                modal: {
                    dataHistory: null,
                    modalHistory: false
                },
                receiptData : null,
                dateRangeConfig: {
                    mode: "range",
                    altFormat: "d-m-Y",
                    dateFormat: "Y-m-d",
                    altInput: true,
                    altInputClass: "flatpickr-alt",
                    onOpen: this.onFlatpickrOpen,
                    onClose: this.onCloseFlatpickr
                },
                data : null,

                options : {
                    status: MAPPING_TIMES_OPTIONS,
                },
                selected: {
                    date: `${start} to ${end}`,
                },
                filter : {
                    start_date: start,
                    end_date: end,
                    times_min: DEFAULT_MAPPING_TIMES.times_min,
                    times_max: DEFAULT_MAPPING_TIMES.times_max,
                    status: null,
                },
                config : {
                    page: 1,
                    limit: 15,
                    lastPage: 0,
                    total: 0,
                },
            }
        },
        components : {
            modal,
            modalHistory,
        },
        mounted () {
            this.fetchPaginated();
        },
        watch : {
            'filter.status': function(newVal) {
                // An empty selection (cleared) falls back to the default option.
                const option = this.options.status.find(item => item.label === newVal) || DEFAULT_MAPPING_TIMES;
                this.filter.times_min = option.times_min;
                this.filter.times_max = option.times_max;
                // The page watcher fetches when the page actually changes.
                if (this.config.page !== 1) {
                    this.config.page = 1;
                    return;
                }
                this.fetchPaginated();
            },
            'filter.start_date': function() {
                this.config.page = 1;
                this.fetchPaginated();
            },
            'filter.end_date': function() {
                this.config.page = 1;
                this.fetchPaginated();
            },
            'config.page' : {
                deep: true,
                handler: 'fetchPaginated'
            },
            engine_no: {
                handler: debounce(function(newVal, oldVal) {
                    if (newVal !== oldVal) {
                        this.config.page = 1;
                        this.fetchPaginated();
                    }
                }, 1000),
            },
        },
        methods : {
            goToPage(page) {
                if (page >= 1 && page <= this.config.lastPage) this.config.page = page;
            },
            setDateFilter: debounce((dates, vm) => {
                vm.filter.start_date = dates[0];
                vm.filter.end_date = dates[1] ? dates[1] : dates[0];
            }, 2000),
            showHistory(data) {
                this.modal.dataHistory = data;
                this.modal.modalHistory = true;
            },
            fetchPaginated () {
                this.filter.engine_no = this.engine_no;
                this.$http.get(`/api/qrs/mapping-repeat`, {params: {...this.filter, ...{page: this.config.page, limit: this.config.limit}}})
                .then(async res => {
                    let data = res.data.data;
                    this.data = data;
                    this.config.lastPage = res.data.meta.lastPage;
                    this.config.total = res.data.meta.total;
                })
                .catch((err)=>console.log('[fetchPaginated] Error:', err));
            },
            onCloseFlatpickr(dates) {
                if (dates.length === 2) {
                    const formatDate = (date) => {
                        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    };
                    const date1 = formatDate(dates[0]);
                    const date2 = formatDate(dates[1]);
                    this.setDateFilter([date1, date2], this);
                    this.afterSelectedFilter = true;
                }   
            },
            onFlatpickrOpen() {
                if (this.afterSelectedFilter) {
                    this.$refs.datepicker.fp.close();
                    this.afterSelectedFilter = false;
                }
            },
        }
    }
</script>

<style>
    .flatpickr-alt {
        font-size: 14px;
        min-width: 260px;
        padding: 0 12px;
        height: 32px;
        background-color: transparent;
        padding-inline-start: 40px;
        cursor: pointer;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
    }

    .qtrust-select {
        min-height: 0;
    }

    .qtrust-select .multiselect__tags {
        min-height: 0;
        height: 32px;
        padding: 4px 40px 0 8px;
    }

    .qtrust-select .multiselect__tags .multiselect__placeholder {
        margin: 0;
        padding: 0;
        font-size: 14px;
        color: black;
    }

    .qtrust-select .multiselect__select {
        height: 32px;
    }

    .qtrust-select .multiselect__select::before {
        z-index: 1;
        margin: 0;
    }

    .qtrust-select .multiselect__single {
        margin: 0;
        font-size: 14px;
    }
</style>

<style scoped>
    .clickAble:hover {
        cursor: pointer;
        opacity: 70%;
    }

    .qtrust-table > thead > tr > th {
        text-align: left;
        border-top: 2px solid #F3F3F3;
        border-bottom: 2px solid #F3F3F3;
        padding: 8px 16px;
        height: 50px;
        vertical-align: middle;
    }

    .qtrust-table > tbody > tr > td {
        text-align: left;
        padding: 8px 16px;
        border-bottom: 1px solid #F3F3F3;
        height: 50px;
        vertical-align: middle;
    }

    .qtrust-custom-select {
        border:1px solid #E8E8E8;
        min-width: 100px;
        /* width: 100px; */
        display: inline-block;
        border-radius: 4px;
        position: relative;
    }
    .qtrust-custom-select select {
        width: 100%;
        height: 100%;
        padding: 2px 5px;
    }
</style>